import { useColorModeValue } from "@chakra-ui/system";
import { motion, AnimatePresence } from "framer-motion";
import Loader from 'views/admin/default/Loader';
import PlanCarousel from '../ZeronineWebsite/PlanCarousel';
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

const PackageExpiredModal = ({ loader, isOpen, onClose }) => {
  const PackageData = useSelector((state) => state.Package?.list || []);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const isDark = useColorModeValue(false, true);

  const handleUpgrade = (planId) => {
    setCurrentPlan(planId);
    setShowUpgrade(false);
    // TODO: Add API call to update subscription
    // dispatch(updateSubscription(planId));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!showUpgrade) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [showUpgrade, onClose]);
  return (
    <AnimatePresence>
      {loader && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={`relative max-w-md w-full mx-4 rounded-lg shadow-2xl p-6 ${isDark
              ? "bg-gray-800"
              : "bg-white"
              }`}
          >
            <Loader />
          </motion.div>
        </motion.div>
      )}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-md"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className={`relative max-w-md w-full mx-4 rounded-2xl shadow-2xl border ${isDark
              ? "bg-gray-800 border-gray-600 shadow-gray-900/50"
              : "bg-white border-gray-200 shadow-gray-400/30"
              }`}
          >
            {/* Header */}
            <div
              className={`relative rounded-t-2xl px-6 py-4 ${isDark
                ? "bg-gradient-to-r from-red-900/30 to-red-800/20"
                : "bg-gradient-to-r from-red-50 to-orange-50"
                }`}
            >
              <div className="flex items-center gap-4">
                <motion.div
                  animate={{ scale: [1, 1.1, 1], rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDark ? "bg-red-500/20" : "bg-red-500/15"
                    } border ${isDark ? "border-red-500/30" : "border-red-200"
                    }`}
                >
                  <svg
                    className="w-7 h-7 text-red-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.div>

                <div className="flex-1">
                  <h2
                    className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Package Expired
                  </h2>
                  <p
                    className={`text-sm mt-1 ${isDark ? "text-red-200" : "text-red-600"
                      }`}
                  >
                    Action required to restore full access
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p
                className={`text-base leading-relaxed mb-3 ${isDark ? "text-gray-200" : "text-gray-700"
                  }`}
              >
                Your subscription period has ended, and access to premium features
                is temporarily paused. To continue enjoying all functionalities,
                please renew your plan. Without renewal, your access will remain
                limited to basic features only.
              </p>

              <div
                className={`text-sm mt-3 p-3 rounded-lg ${isDark
                  ? "bg-gray-700/50 text-gray-300"
                  : "bg-gray-50 text-gray-600"
                  }`}
              >
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    Renew your current plan to unlock all features again.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    Get instant access — no data or settings will be lost.
                  </li>
                </ul>
              </div>

              {/* Buttons */}
              <div className="mt-6 space-y-4">
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={onClose}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 border ${isDark
                      ? "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-gray-500"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                      }`}
                  >
                    Close
                  </button>

                  <button
                    onClick={() => setShowUpgrade(true)}
                    className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-red-500/25"
                  >
                    Renew Plan
                  </button>
                </div>
              </div>

              <p
                className={`text-xs text-center mt-4 pt-3 border-t ${isDark
                  ? "text-gray-500 border-gray-700"
                  : "text-gray-400 border-gray-200"
                  }`}
              >
                Need help?{" "}
                {/* <Link to={'/helpsupport'}> */}
                <a
                  href="/helpsupport"
                  className={`underline hover:no-underline ${isDark ? "text-red-400" : "text-red-500"
                    }`}
                >
                  Contact support
                </a>
                {/* </Link> */}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
      {showUpgrade && (
        <div className="fixed inset-0 z-[1001]">
          <PlanCarousel
            PackageData={PackageData}
            currentPlan={currentPlan}
            onUpgrade={handleUpgrade}
            onClose={() => setShowUpgrade(false)}
            isExpired={true}
          />
        </div>
      )}
    </AnimatePresence>
  );
};

export default PackageExpiredModal;
